import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  Lightbulb,
  CheckCircle,
  ChevronRight,
  Sparkles,
  Layers,
  AlertTriangle,
  Cpu
} from 'lucide-react';
import innovationIdeasDataRaw from '../../data/innovationIdeas.json';
import type { InnovationIdeasData } from '../../types/innovationIdeas';

import { TrizIdeaParser, type Top10Idea, type ParsedData } from '../../json parser/text_to_json';

// ========================================
// TEST MODE CONFIGURATION
// ========================================
// Uncomment the line below to enable test mode
// const TEST_MODE = true;

// Comment the line below to disable production mode
 const TEST_MODE = false;

// Test file path (relative to public folder or import)
const TEST_FILE_PATH = '/skyflow_triz_analysis_20250715_235453.txt';

// ========================================
// END TEST MODE CONFIGURATION
// ========================================

interface TRIZPrinciple {
  id: number;
  name: string;
  description: string;
  contradiction: string;
  application: string;
  examples: string[];
  innovationLevel: number;
}

interface GeneratedIdea {
  id: string;
  title: string;
  description: string;
  trizPrinciples: number[];
  category: string;
  innovationScore: number;
  feasibilityScore: number;
  impactScore: number;
  generatedBy: string;
  reasoning: string;
  implementationSteps: string[];
}

interface TrizIntegrationPhaseProps {
  companyData: any;
  researchResults: any;
  onComplete: (results: any) => void;
  initialState?: {
    generatedIdeas?: GeneratedIdea[];
    trizAgentResponse?: string;
    currentStage?: number;
    stageProgress?: number;
    isCompleted?: boolean;
    selectedIdea?: GeneratedIdea | null;
    parsingError?: string | null;
  };
  onStateChange?: (state: any) => void;
  isCompleted?: boolean;
  phaseResults?: any;
}

const innovationIdeasData = innovationIdeasDataRaw as InnovationIdeasData;

const TrizIntegrationPhase: React.FC<TrizIntegrationPhaseProps> = ({
  onComplete,
  initialState,
  onStateChange,
  isCompleted,
  phaseResults
}) => {
  const [searchParams] = useSearchParams();
  const [currentStage, setCurrentStage] = useState(initialState?.currentStage || 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingLogs, setProcessingLogs] = useState<string[]>([]);
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[]>(initialState?.generatedIdeas || []);
  const [isParsingGcsFile, setIsParsingGcsFile] = useState(false);
  const [gcsFileError, setGcsFileError] = useState<string | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<GeneratedIdea | null>(initialState?.selectedIdea || null);
  const [stageProgress, setStageProgress] = useState(initialState?.stageProgress || 0);
  const [isIntegrationCompleted, setIsIntegrationCompleted] = useState(initialState?.isCompleted || false);
  const [streamingResults, setStreamingResults] = useState<any>({});
  const [parsingError, setParsingError] = useState<string | null>(initialState?.parsingError || null);

  const [trizAgentResponse, setTrizAgentResponse] = useState<string>(initialState?.trizAgentResponse || "");
  const [isTrizAgentApiCallLoading, setIsTrizAgentApiCallLoading] = useState(false);
  
  // Debug initial state
  console.log('TrizIntegrationPhase - Component initialized:', {
    hasInitialState: !!initialState,
    initialTrizResponse: initialState?.trizAgentResponse?.length || 0,
    isCompleted: isCompleted,
    hasPhaseResults: !!phaseResults
  });

  // Add test mode indicator to logs
  useEffect(() => {
    if (TEST_MODE) {
      console.log('🧪 TEST MODE ENABLED - Will read from local file:', TEST_FILE_PATH);
      console.log('🔄 To switch to production mode, comment out "const TEST_MODE = true;" and uncomment "const TEST_MODE = false;"');
    } else {
      console.log('🚀 PRODUCTION MODE - Will read from GCS URL');
      console.log('🔄 To switch to test mode, comment out "const TEST_MODE = false;" and uncomment "const TEST_MODE = true;"');
    }
  }, []);

  // Get the objective from URL parameters or use default
  const objective = searchParams.get('objective') || 
    "We want to expand our data privacy platform globally while maintaining 99.99% uptime, achieving SOC2 compliance in all regions, and reducing operational costs by 30%. Our goal is to become the leading privacy infrastructure provider for enterprises handling sensitive data.";

  // If the phase is already completed, show the results immediately
  useEffect(() => {
    if (isCompleted && phaseResults) {
      setIsIntegrationCompleted(true);
      setCurrentStage(6); // Last stage
      setStageProgress(100);
      setGeneratedIdeas(phaseResults.generatedIdeas || []);
      setTrizAgentResponse(phaseResults.trizAgentResponse || "");
    }
  }, [isCompleted, phaseResults]);

  const trizAgentResponseGcsUrl = useMemo(() => {
    if(!isTrizAgentApiCallLoading) {
      const  trizAgentResponseGcsUrl = trizAgentResponse?.match(/\[GCS File]\((.*?)\)/);
      let gcsUrl = "";
      if (trizAgentResponseGcsUrl?.length) {
        gcsUrl = trizAgentResponseGcsUrl[1];
      }
      console.log('TrizIntegrationPhase - GCS URL detection:', {
        hasResponse: !!trizAgentResponse,
        responseLength: trizAgentResponse?.length || 0,
        gcsPattern: !!trizAgentResponseGcsUrl,
        gcsUrl: gcsUrl,
        isLoading: isTrizAgentApiCallLoading
      });
      return gcsUrl
    }
    return "";
  }, [trizAgentResponse, isTrizAgentApiCallLoading]);

  // Effect to fetch and parse GCS file when URL is available, or parse TRIZ response directly
  useEffect(() => {
    console.log('TrizIntegrationPhase - GCS file effect triggered:', {
      hasGcsUrl: !!trizAgentResponseGcsUrl,
      gcsUrl: trizAgentResponseGcsUrl,
      isParsingGcsFile: isParsingGcsFile,
      hasTrizResponse: !!trizAgentResponse,
      trizResponseLength: trizAgentResponse?.length || 0,
      isCompleted: isIntegrationCompleted
    });
    
    if (trizAgentResponseGcsUrl && !isParsingGcsFile) {
      console.log('TrizIntegrationPhase - About to fetch and parse GCS file');
      fetchAndParseGcsFile(trizAgentResponseGcsUrl);
    } else if (trizAgentResponse && trizAgentResponse.length > 0 && !isParsingGcsFile && generatedIdeas.length === 0 && !isTrizAgentApiCallLoading) {
      console.log('TrizIntegrationPhase - No GCS URL found, parsing TRIZ response directly');
      parseActualTrizContent();
    }
  }, [trizAgentResponseGcsUrl, isParsingGcsFile, trizAgentResponse, generatedIdeas.length, isTrizAgentApiCallLoading]);

  // Effect to notify about state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        generatedIdeas,
        trizAgentResponse,
        currentStage,
        stageProgress,
        isIntegrationCompleted,
        selectedIdea,
        parsingError
      });
    }
  }, [generatedIdeas, trizAgentResponse, currentStage, stageProgress, isIntegrationCompleted, selectedIdea, parsingError]);

  const trizAgentLogsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (trizAgentLogsRef.current) {
      trizAgentLogsRef.current.scrollTo({
                    top: trizAgentLogsRef.current.scrollHeight,
                    behavior: 'smooth', // 👈 Smooth scrolling
                });
    }
  }, [trizAgentResponse]);

  // Auto-transition when progress reaches 100%
  useEffect(() => {
    if (stageProgress >= 100 && !isTrizAgentApiCallLoading && isProcessing) {
      // Remove the delay or make it very small
      setIsProcessing(false);
    }
  }, [stageProgress, isTrizAgentApiCallLoading, isProcessing]); 

  const trizIdeasStages = [
    { name: 'Analysis user problem', icon: Layers, duration: 4000, progress: 0 },
    { name: 'Problem Understanding & Definition', icon: Layers, duration: 4000, progress: 10 },
    { name: 'Problem Analysis & Decomposition', icon: AlertTriangle, duration: 3500, progress: 30 },
    { name: 'Systemic Analysis & Evolution', icon: Cpu, duration: 4500, progress: 40 },
    { name: 'Bottleneck Identification & Resolution', icon: Lightbulb, duration: 3000, progress: 60 },
    { name: 'TRIZ Principle-Based Ideas', icon: Lightbulb, duration: 3000, progress: 70 },
    { name: 'Advanced TRIZ Solutions', icon: Sparkles, duration: 3000, progress: 90 }
  ];

  const getAndSetStageProgress = (data: string) => {
    if(data?.includes("USER & CONTRADICTION ANALYSIS")) {
      setCurrentStage(1);
      setStageProgress(trizIdeasStages[1].progress);
      return
    }
    if(data?.includes("PROBLEM DECOMPOSITION")) {
      setCurrentStage(2);
      setStageProgress(trizIdeasStages[2].progress);
      return
    }
    if(data?.includes("SYSTEM OPERATOR GRID ")) {
      setCurrentStage(3);
      setStageProgress(trizIdeasStages[3].progress);
      return
    }
    if(data?.includes("BOTTLENECK ANALYSIS")) {
      setCurrentStage(4);
      setStageProgress(trizIdeasStages[4].progress);
      return
    }
    if(data?.includes("INDUSTRY ANALYSIS")) {
      setCurrentStage(5);
      setStageProgress(trizIdeasStages[5].progress);
      return
    }
    if(data?.includes("CREATIVE COMBINATION MATRIX")) {
      setCurrentStage(6);
      setStageProgress(trizIdeasStages[6].progress);
      return
    }

    setStageProgress(oldState => {
      if (oldState > 90) {
        setCurrentStage(6);
      } else if (oldState > 70) {
        setCurrentStage(5);
      } else if (oldState > 60) {
        setCurrentStage(4);
      } else if (oldState > 40) {
        setCurrentStage(3);
      } else if (oldState > 30) {
        setCurrentStage(2);
      } else if (oldState > 10) {
        setCurrentStage(1);
      }
      
      // Cap at 95% instead of 100% to leave room for parsing
      if(oldState >= 95) {
        return 95;  // Don't complete until parsing is done
      }
      if(oldState + 2 > 85) {
        return oldState + 1;
      }
      return oldState + 2
    });
  }

  const getTrizAgentResponse = async () => {
    setTrizAgentResponse("");
    setIsProcessing(true);
    setStageProgress(0);
    setIsTrizAgentApiCallLoading(true);

    const query = {
      query: objective
    };

    const res = await fetch(`${(import.meta as any).env.VITE_API_URL}/analyze/triz`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(query)
    });

    if (!res.body) {
      console.error("No stream found");
      setIsTrizAgentApiCallLoading(false);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        getAndSetStageProgress(chunk);
        setTrizAgentResponse((prev) => prev + chunk);
      }
    } catch (err) {
      console.error("Stream error:", err);
    } finally {
      setIsTrizAgentApiCallLoading(false);
      setCurrentStage(6);  // Set to final stage
      setStageProgress(95);  // Set to 95% instead of 100% - parsing still needs to happen
      console.log('TrizIntegrationPhase - TRIZ agent response completed, parsing will be triggered by useEffect');
    }
  };

  const trizPrinciples: TRIZPrinciple[] = [
    {
      id: 1,
      name: 'Segmentation',
      description: 'Divide an object into independent parts',
      contradiction: 'Complexity vs Simplicity',
      application: 'Modular PII detection engines for different data types',
      examples: ['Separate text, image, and audio PII detectors', 'Modular compliance modules', 'Independent classification pipelines'],
      innovationLevel: 7.5
    },
    {
      id: 2,
      name: 'Taking Out',
      description: 'Separate interfering parts or properties',
      contradiction: 'Performance vs Features',
      application: 'Isolate sensitive data processing from main application flow',
      examples: ['Dedicated privacy processing layer', 'Separate compliance audit trail', 'Isolated sensitive data storage'],
      innovationLevel: 8.2
    },
    {
      id: 15,
      name: 'Dynamics',
      description: 'Make objects or systems adaptive',
      contradiction: 'Stability vs Adaptability',
      application: 'Adaptive PII classification based on context and regulations',
      examples: ['Context-aware privacy scoring', 'Dynamic compliance rules', 'Adaptive sensitivity thresholds'],
      innovationLevel: 9.1
    },
    {
      id: 17,
      name: 'Another Dimension',
      description: 'Move to multi-dimensional space',
      contradiction: 'Single vs Multiple perspectives',
      application: 'Multi-dimensional privacy analysis (time, geography, context)',
      examples: ['Temporal privacy patterns', 'Geographic compliance mapping', 'Contextual sensitivity analysis'],
      innovationLevel: 8.7
    },
    {
      id: 35,
      name: 'Parameter Changes',
      description: 'Change physical or chemical states',
      contradiction: 'Fixed vs Variable parameters',
      application: 'Transform data representation for better privacy detection',
      examples: ['Encrypted computation on sensitive data', 'Privacy-preserving data transformations', 'Homomorphic encryption processing'],
      innovationLevel: 9.3
    },
    {
      id: 40,
      name: 'Composite Materials',
      description: 'Use composite structures',
      contradiction: 'Uniformity vs Diversity',
      application: 'Hybrid privacy protection combining multiple techniques',
      examples: ['ML + rule-based detection', 'Multi-vendor privacy stack', 'Layered privacy architecture'],
      innovationLevel: 8.4
    }
  ];

  const stages = [
    { name: 'TRIZ Principles Application', icon: Layers, duration: 4000, progress: 20 },
    { name: 'Contradiction Analysis', icon: AlertTriangle, duration: 3500, progress: 40 },
    { name: 'Mechanized Thinking Process', icon: Cpu, duration: 5000, progress: 60 },
    { name: 'Seed Ideas Generation', icon: Lightbulb, duration: 4500, progress: 80 },
    { name: 'LLM Enhancement', icon: Sparkles, duration: 3000, progress: 100 }
  ];

  const generateContradictions = () => {
    return [
      {
        improving: 'Detection Accuracy',
        worsening: 'Processing Speed',
        principles: [1, 15, 35],
        description: 'Higher accuracy models require more computational resources',
        solutions: [
          'Implement hierarchical detection (fast screening + detailed analysis)',
          'Use adaptive processing based on data sensitivity',
          'Deploy edge computing for local processing'
        ]
      },
      {
        improving: 'Regulatory Compliance',
        worsening: 'System Flexibility',
        principles: [2, 17, 40],
        description: 'Strict compliance rules limit system adaptability',
        solutions: [
          'Modular compliance framework with pluggable rules',
          'Multi-dimensional compliance mapping',
          'Hybrid governance with automated and manual processes'
        ]
      },
      {
        improving: 'Data Privacy',
        worsening: 'Feature Richness',
        principles: [15, 35],
        description: 'Enhanced privacy protection limits available features',
        solutions: [
          'Privacy-preserving feature engineering',
          'Differential privacy techniques',
          'Federated learning for model improvement'
        ]
      }
    ];
  };

  const loadIdeasFromJson = () => {
    // Convert JSON ideas to GeneratedIdea format
    const convertedIdeas: GeneratedIdea[] = innovationIdeasData.ideas.map((idea) => ({
      id: `idea-${idea.id}`,
      title: idea.title,
      description: idea.description,
      trizPrinciples: idea.trizPrinciples,
      category: idea.category,
      innovationScore: idea.score,
      feasibilityScore: idea.difficulty === 'Low' ? 9.0 : idea.difficulty === 'Medium' ? 7.5 : idea.difficulty === 'High' ? 6.0 : 4.5,
      impactScore: idea.impact === 'Revolutionary' ? 9.5 : idea.impact === 'High' ? 8.0 : idea.impact === 'Medium' ? 6.5 : 5.0,
      generatedBy: 'TRIZ-Enhanced JSON Database',
      reasoning: `Advanced innovation leveraging TRIZ principles ${idea.trizPrinciples.join(', ')} for ${idea.category.toLowerCase()} enhancement`,
      implementationSteps: [
        'Conduct detailed feasibility analysis',
        'Design comprehensive technical architecture',
        'Implement core algorithms and systems',
        'Validate solution through testing',
        'Deploy and monitor performance metrics'
      ]
    }));

    return convertedIdeas;
  };

  const [parsedTrizData, setParsedTrizData] = useState<ParsedData | null>(null);

  const parseActualTrizContent = async () => {
    setIsParsingGcsFile(true);
    setGcsFileError(null);
    
    try {
      // Use the actual TRIZ agent response content for parsing
      console.log('Processing actual TRIZ content from agent response...');
      
      if (!trizAgentResponse || trizAgentResponse.trim() === '') {
        throw new Error('No TRIZ agent response content available');
      }
      
      // Parse the actual text content using TrizIdeaParser
      const parser = new TrizIdeaParser();
      console.log('TrizIntegrationPhase - About to parse TRIZ response with length:', trizAgentResponse.length);
      const parsedData = parser.parse(trizAgentResponse);
      
      console.log('TrizIntegrationPhase - Parsed TRIZ data:', parsedData);
      console.log('TrizIntegrationPhase - Parsed data has top10Ideas:', {
        hasTop10Ideas: !!parsedData.top10Ideas,
        top10IdeasLength: parsedData.top10Ideas?.length,
        top10IdeasArray: Array.isArray(parsedData.top10Ideas),
        top10IdeasContent: parsedData.top10Ideas
      });
      
      // If top 10 ideas are incomplete, try dedicated parser
      if (!parsedData.top10Ideas || parsedData.top10Ideas.length < 10) {
        console.log('TrizIntegrationPhase - Top 10 ideas incomplete, trying dedicated parser');
        try {
          const { top10Ideas: dedicatedTop10, summary } = parser.parseOnlyTop10Ideas(trizAgentResponse);
          if (dedicatedTop10.length > (parsedData.top10Ideas?.length || 0)) {
            console.log('TrizIntegrationPhase - Dedicated parser found more ideas:', summary);
            parsedData.top10Ideas = dedicatedTop10;
          }
        } catch (dedicatedError) {
          console.error('TrizIntegrationPhase - Dedicated parser also failed:', dedicatedError);
        }
      }
      
      // Store the parsed data for later use
      setParsedTrizData(parsedData);
      
      console.log('TrizIntegrationPhase - Parsed data:', {
        hasTop10Ideas: parsedData.top10Ideas && parsedData.top10Ideas.length > 0,
        top10IdeasCount: parsedData.top10Ideas?.length,
        top10Ideas: parsedData.top10Ideas,
        top10IdeasRanks: parsedData.top10Ideas?.map((idea: Top10Idea) => idea.rank).sort((a, b) => a - b),
        top10IdeasScores: parsedData.top10Ideas?.map((idea: Top10Idea) => ({
          name: idea.name,
          rank: idea.rank,
          overall_score: idea.overall_score,
          individual_scores: idea.individual_scores
        })),
        marketResearch: parsedData.marketResearch,
        keyValuePoints: parsedData.keyValuePoints,
        allParsedDataKeys: Object.keys(parsedData),
        sectionsKeys: parsedData.sections ? Object.keys(parsedData.sections) : [],
        principlesCount: parsedData.principles?.length || 0
      });
      
      // Debug section ideas
      if (parsedData.sections) {
        console.log('Section ideas:', Object.entries(parsedData.sections).map(([section, ideas]) => ({
          section,
          ideasCount: (ideas as any[]).length,
          firstIdeaNames: (ideas as any[]).slice(0, 3).map(idea => idea.ideaName || 'No name')
        })));
      } else {
        console.log('No sections found in parsed data');
      }
      
      // Check if we got any ideas (either top 10 or regular sections)
      const hasSections = parsedData.sections && Object.keys(parsedData.sections).length > 0;
      const hasTop10Ideas = parsedData.top10Ideas && parsedData.top10Ideas.length > 0;
      
      console.log('TrizIntegrationPhase - Validation check:', {
        hasSections,
        hasTop10Ideas,
        sectionsCount: parsedData.sections ? Object.keys(parsedData.sections).length : 0,
        top10IdeasCount: parsedData.top10Ideas?.length || 0
      });
      
      if (!hasSections && !hasTop10Ideas) {
        console.error('TrizIntegrationPhase - No valid TRIZ ideas found in the response');
        throw new Error('No valid TRIZ ideas found in the response');
      }
      
      // Convert parsed ideas to GeneratedIdea format
      const convertedIdeas = convertParsedIdeasToGeneratedIdeas(parsedData);
      
      if (convertedIdeas.length === 0) {
        console.warn('TrizIntegrationPhase - No ideas could be converted from the parsed data!');
        console.warn('TrizIntegrationPhase - Parsed data summary:', {
          sectionsKeys: parsedData.sections ? Object.keys(parsedData.sections) : [],
          top10IdeasCount: parsedData.top10Ideas?.length || 0,
          principlesCount: parsedData.principles?.length || 0,
          totalIdeas: parsedData.totalIdeas || 0
        });
        // Don't throw error here, just continue with empty ideas
      }
      
      console.log('TrizIntegrationPhase - Converted ideas:', convertedIdeas.map(idea => ({ id: idea.id, title: idea.title })));
      
      // Set the ideas immediately so they show up in the UI
      setGeneratedIdeas(convertedIdeas);
      
      // NOW set progress to 100% and mark as completed
      setStageProgress(100);
      setIsProcessing(false);
      
      // Mark as completed only after ideas are set
      setTimeout(() => {
        setIsIntegrationCompleted(true);
      }, 100);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error parsing actual TRIZ content:', error);
      setGcsFileError(errorMessage);
      setParsingError(errorMessage);
      
      // Fallback to JSON data if parsing fails
      const fallbackIdeas = loadIdeasFromJson();
      setGeneratedIdeas(fallbackIdeas);
      
      // Set progress to 100% even on error
      setStageProgress(100);
      setIsProcessing(false);
      
      setTimeout(() => {
        setIsIntegrationCompleted(true);
      }, 100);
    } finally {
      setIsParsingGcsFile(false);
    }
  };

  const fetchAndParseGcsFile = async (gcsUrl: string) => {
    console.log('fetchAndParseGcsFile called with:', { gcsUrl, TEST_MODE });
    
    if (TEST_MODE) {
      console.log('🧪 TEST MODE: Reading from local file instead of GCS URL');
      await fetchAndParseLocalFile();
    } else {
      console.log('🚀 PRODUCTION MODE: Reading from GCS URL');
      // In production, we actually just parse the TRIZ content from the agent response
      await parseActualTrizContent();
    }
  };

  const fetchAndParseLocalFile = async () => {
    setIsParsingGcsFile(true);
    setGcsFileError(null);
    
    try {
      console.log('🧪 TEST MODE: Fetching local file:', TEST_FILE_PATH);
      
      // Simulate stage progression for better UX
      const simulateStageProgression = () => {
        return new Promise<void>((resolve) => {
          let progress = 0;
          const interval = setInterval(() => {
            progress += 15;
            if (progress >= 95) {
              clearInterval(interval);
              setStageProgress(95);
              setCurrentStage(6);
              resolve();
            } else {
              setStageProgress(progress);
              setCurrentStage(Math.min(Math.floor(progress / 15), 6));
            }
          }, 200); // 200ms intervals for smooth progression
        });
      };
      
      // Start stage progression
      await simulateStageProgression();
      
      // Fetch the local file from the public directory
      const response = await fetch(TEST_FILE_PATH);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch local test file: ${response.status} ${response.statusText}`);
      }
      
      const testContent = await response.text();
      console.log('🧪 TEST MODE: Local file content loaded, length:', testContent.length);
      
      // Parse the test content using TrizIdeaParser
      const parser = new TrizIdeaParser();
      console.log('🧪 TEST MODE: About to parse test content with length:', testContent.length);
      const parsedData = parser.parse(testContent);
      
      console.log('🧪 TEST MODE: Parsed test data:', parsedData);
      console.log('🧪 TEST MODE: Parsed data has top10Ideas:', {
        hasTop10Ideas: !!parsedData.top10Ideas,
        top10IdeasLength: parsedData.top10Ideas?.length,
        top10IdeasArray: Array.isArray(parsedData.top10Ideas),
        top10IdeasContent: parsedData.top10Ideas
      });
      
      // If top 10 ideas are incomplete, try dedicated parser
      if (!parsedData.top10Ideas || parsedData.top10Ideas.length < 10) {
        console.log('🧪 TEST MODE: Top 10 ideas incomplete, trying dedicated parser');
        try {
          const { top10Ideas: dedicatedTop10, summary } = parser.parseOnlyTop10Ideas(testContent);
          if (dedicatedTop10.length > (parsedData.top10Ideas?.length || 0)) {
            console.log('🧪 TEST MODE: Dedicated parser found more ideas:', summary);
            parsedData.top10Ideas = dedicatedTop10;
          }
        } catch (dedicatedError) {
          console.error('🧪 TEST MODE: Dedicated parser also failed:', dedicatedError);
        }
      }
      
      // Store the parsed data for later use
      setParsedTrizData(parsedData);
      
      console.log('🧪 TEST MODE: Parsed data:', {
        hasTop10Ideas: parsedData.top10Ideas && parsedData.top10Ideas.length > 0,
        top10IdeasCount: parsedData.top10Ideas?.length,
        top10Ideas: parsedData.top10Ideas,
        top10IdeasRanks: parsedData.top10Ideas?.map((idea: Top10Idea) => idea.rank).sort((a, b) => a - b),
        top10IdeasScores: parsedData.top10Ideas?.map((idea: Top10Idea) => ({
          name: idea.name,
          rank: idea.rank,
          overall_score: idea.overall_score,
          individual_scores: idea.individual_scores
        })),
        marketResearch: parsedData.marketResearch,
        keyValuePoints: parsedData.keyValuePoints,
        allParsedDataKeys: Object.keys(parsedData),
        sectionsKeys: parsedData.sections ? Object.keys(parsedData.sections) : [],
        principlesCount: parsedData.principles?.length || 0
      });
      
      // Check if we got any ideas (either top 10 or regular sections)
      const hasSections = parsedData.sections && Object.keys(parsedData.sections).length > 0;
      const hasTop10Ideas = parsedData.top10Ideas && parsedData.top10Ideas.length > 0;
      
      console.log('🧪 TEST MODE: Validation check:', {
        hasSections,
        hasTop10Ideas,
        sectionsCount: parsedData.sections ? Object.keys(parsedData.sections).length : 0,
        top10IdeasCount: parsedData.top10Ideas?.length || 0
      });
      
      if (!hasSections && !hasTop10Ideas) {
        console.error('🧪 TEST MODE: No valid TRIZ ideas found in the test file');
        throw new Error('No valid TRIZ ideas found in the test file');
      }
      
      // Convert parsed ideas to GeneratedIdea format
      const convertedIdeas = convertParsedIdeasToGeneratedIdeas(parsedData);
      
      if (convertedIdeas.length === 0) {
        console.warn('🧪 TEST MODE: No ideas could be converted from the parsed test data!');
        console.warn('🧪 TEST MODE: Parsed data summary:', {
          sectionsKeys: parsedData.sections ? Object.keys(parsedData.sections) : [],
          top10IdeasCount: parsedData.top10Ideas?.length || 0,
          principlesCount: parsedData.principles?.length || 0,
          totalIdeas: parsedData.totalIdeas || 0
        });
      }
      
      console.log('🧪 TEST MODE: Converted ideas:', convertedIdeas.map(idea => ({ id: idea.id, title: idea.title })));
      
      // Set the test content as the TRIZ agent response for display
      setTrizAgentResponse(testContent);
      
      // Set the ideas immediately so they show up in the UI
      setGeneratedIdeas(convertedIdeas);
      
      // NOW set progress to 100% and mark as completed
      setStageProgress(100);
      setIsProcessing(false);
      
      // Mark as completed only after ideas are set
      setTimeout(() => {
        setIsIntegrationCompleted(true);
      }, 100);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('🧪 TEST MODE: Error parsing test file:', error);
      setGcsFileError(errorMessage);
      setParsingError(errorMessage);
      
      // Fallback to JSON data if parsing fails
      const fallbackIdeas = loadIdeasFromJson();
      setGeneratedIdeas(fallbackIdeas);
      
      // Set progress to 100% even on error
      setStageProgress(100);
      setIsProcessing(false);
      
      setTimeout(() => {
        setIsIntegrationCompleted(true);
      }, 100);
    } finally {
      setIsParsingGcsFile(false);
    }
  };

  const convertParsedIdeasToGeneratedIdeas = (parsedData: ParsedData): GeneratedIdea[] => {
    const ideas: GeneratedIdea[] = [];
    let ideaCounter = 1;

    console.log('Converting parsed ideas to generated ideas. Input data:', {
      hasSections: !!parsedData.sections,
      sectionsCount: parsedData.sections ? Object.keys(parsedData.sections).length : 0,
      hasTop10Ideas: !!parsedData.top10Ideas,
      top10IdeasCount: parsedData.top10Ideas?.length || 0,
      hasPrinciples: !!parsedData.principles,
      principlesCount: parsedData.principles?.length || 0
    });

    const principleNameToId = trizPrinciples.reduce((acc, p) => {
        acc[p.name.toLowerCase()] = p.id;
        return acc;
    }, {} as Record<string, number>);

    // Convert each section's ideas to GeneratedIdea format
    if (parsedData.sections && typeof parsedData.sections === 'object') {
      Object.entries(parsedData.sections).forEach(([sectionName, sectionIdeas]) => {
        if (Array.isArray(sectionIdeas) && sectionIdeas.length > 0) {
          (sectionIdeas as any[]).forEach((idea) => {
        
        let principles: number[] = [];
        if (idea.principles) {
            principles = idea.principles.split(',').map((p: string) => {
                const principleName = p.trim().toLowerCase();
                return principleNameToId[principleName] || 0;
            }).filter((id: number) => id > 0);
        } else if (idea.combination) {
             principles = idea.combination.split(' + ').map((p: string) => {
                const principleName = p.trim().toLowerCase().replace('principle \\d+: ', '');
                return principleNameToId[principleName] || 0;
            }).filter((id: number) => id > 0);
        }

        const generatedIdea: GeneratedIdea = {
          id: `parsed-idea-${ideaCounter++}`,
          title: idea.ideaName || "Untitled Idea",
          description: idea.description,
          trizPrinciples: principles,
          category: sectionName,
          innovationScore: Math.random() * 3 + 7, // Random score between 7-10
          feasibilityScore: Math.random() * 3 + 6, // Random score between 6-9
          impactScore: Math.random() * 3 + 7, // Random score between 7-10
          generatedBy: 'TRIZ Agent Analysis',
          reasoning: idea.synergy || `Generated from ${sectionName} using TRIZ methodology${idea.principles ? ` - ${idea.principles}` : ''}`,
          implementationSteps: idea.implementation ? 
            idea.implementation.split(/[.!?]/).filter((step: string) => step.trim().length > 0).map((step: string) => step.trim()) :
            [
              'Analyze feasibility and requirements',
              'Design technical architecture',
              'Implement core functionality',
              'Test and validate solution',
              'Deploy and monitor performance'
            ]
        };
        ideas.push(generatedIdea);
          });
        }
      });
    }

    // Convert top 10 ideas to GeneratedIdea format
    if (parsedData.top10Ideas && parsedData.top10Ideas.length > 0) {
      console.log('TrizIntegrationPhase - Converting top 10 ideas:', {
        count: parsedData.top10Ideas.length,
        firstIdea: parsedData.top10Ideas[0],
        firstIdeaKeys: Object.keys(parsedData.top10Ideas[0])
      });
      
      // COMMENTED OUT: Don't show top 10 ideas in the Generated Innovation Ideas section
      /*
      parsedData.top10Ideas.forEach((top10Idea: Top10Idea) => {
        const generatedIdea: GeneratedIdea = {
          id: `top10-idea-${ideaCounter++}`,
          title: top10Idea.name || "Untitled Top Idea",
          description: top10Idea.key_strengths ? top10Idea.key_strengths.join('. ') : "High-ranking innovative solution",
          trizPrinciples: [], // Top 10 ideas may not have specific principles mapped
          category: `Top 10 Ideas (Rank ${top10Idea.rank})`,
          innovationScore: top10Idea.overall_score || 8.0,
          feasibilityScore: top10Idea.individual_scores?.find(s => s.criterion === 'Technical Feasibility')?.score || 7.0,
          impactScore: top10Idea.individual_scores?.find(s => s.criterion === 'Market Impact')?.score || 8.0,
          generatedBy: `TRIZ Top 10 Analysis - ${top10Idea.original_source || 'Unknown'}`,
          reasoning: `Top-ranked idea (${top10Idea.rank}/10) with overall score ${top10Idea.overall_score}. ${top10Idea.areas_for_improvement ? 'Areas for improvement: ' + top10Idea.areas_for_improvement.join(', ') : ''}`,
          implementationSteps: [
            'Conduct detailed feasibility analysis',
            'Address areas for improvement identified in analysis',
            'Develop prototype or proof of concept',
            'Validate market demand and technical viability',
            'Scale implementation based on success metrics'
          ]
        };
        ideas.push(generatedIdea);
      });
      */
    }

    // Convert TRIZ principles breakthrough applications to GeneratedIdea format
    if (parsedData.principles && Array.isArray(parsedData.principles)) {
      parsedData.principles.forEach((principle: any) => {
        if (principle.breakthrough_applications && Array.isArray(principle.breakthrough_applications)) {
          principle.breakthrough_applications.forEach((app: any) => {
            const generatedIdea: GeneratedIdea = {
              id: `principle-idea-${ideaCounter++}`,
              title: app.idea_name || "Untitled Principle Application",
              description: app.description || "",
              trizPrinciples: [principle.principle_number || 0].filter(id => id > 0),
              category: `TRIZ Principle ${principle.principle_number}: ${principle.principle_name}`,
              innovationScore: Math.random() * 2 + 8, // Higher scores for breakthrough applications
              feasibilityScore: Math.random() * 3 + 6,
              impactScore: Math.random() * 2 + 8,
              generatedBy: `TRIZ Principle ${principle.principle_number} Application`,
              reasoning: `Breakthrough application of TRIZ Principle ${principle.principle_number} (${principle.principle_name}). ${app.contradiction_resolved || ''}`,
              implementationSteps: app.implementation ? 
                app.implementation.split(/[.!?]/).filter((step: string) => step.trim().length > 0).map((step: string) => step.trim()) :
                [
                  'Study the specific TRIZ principle application',
                  'Analyze contradictions being resolved',
                  'Design solution architecture',
                  'Implement breakthrough technology',
                  'Validate breakthrough impact'
                ]
            };
            ideas.push(generatedIdea);
          });
        }
      });
    }
    
    // If no ideas were generated from any source, add a fallback
    if (ideas.length === 0) {
      console.warn('No ideas were generated from TRIZ parsing, adding fallback ideas');
      const fallbackIdeas = loadIdeasFromJson();
      ideas.push(...fallbackIdeas);
    }
    
    console.log(`Total ideas generated: ${ideas.length}`);
    return ideas;
  };

  const startProgressiveTrizProcessing = async () => {
    console.log('TrizIntegrationPhase - startProgressiveTrizProcessing called');
    console.log('TrizIntegrationPhase - trizAgentResponse details:', {
      exists: !!trizAgentResponse,
      length: trizAgentResponse?.length || 0,
      isEmpty: !trizAgentResponse || trizAgentResponse.trim() === '',
      preview: trizAgentResponse?.substring(0, 200) || 'No response'
    });
    
    setIsProcessing(true); 
    
    // First try to parse the actual TRIZ content from the response
    if (trizAgentResponse && trizAgentResponse.trim() !== '') {
      try {
        await parseActualTrizContent();
      } catch (error) {
        console.error('TrizIntegrationPhase - Failed to parse actual TRIZ content:', error);
        console.error('TrizIntegrationPhase - Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          trizResponseLength: trizAgentResponse?.length || 0,
          trizResponsePreview: trizAgentResponse?.substring(0, 500) || ''
        });
        console.log('TrizIntegrationPhase - Falling back to JSON hardcoded ideas');
        // If parsing fails, fallback to JSON
        const ideas = loadIdeasFromJson();
        setGeneratedIdeas(ideas);
        setTimeout(() => {
          setIsIntegrationCompleted(true);
        }, 100);
      }
    } else {
      // If no agent response available, use JSON fallback
      const ideas = loadIdeasFromJson();
      setGeneratedIdeas(ideas);
      setTimeout(() => {
        setIsIntegrationCompleted(true);
      }, 100);
    }
    
    setIsProcessing(false);
  };

  // Generate stage-specific results for streaming
  const getStageResults = (stageName: string, completion: number) => {
    switch (stageName) {
      case 'TRIZ Principles Application':
        return {
          principlesAnalyzed: Math.round(6 * completion),
          contradictionsMapped: Math.round(8 * completion),
          transformationPatterns: Math.round(12 * completion)
        };
      case 'Contradiction Analysis':
        return {
          contradictionsIdentified: Math.round(3 * completion),
          resolutionStrategies: Math.round(15 * completion)
        };
      case 'Mechanized Thinking Process':
        return {
          conceptsGenerated: Math.round(2847 * completion),
          transformationRules: Math.round(40 * completion)
        };
      case 'Seed Ideas Generation':
        return {
          refinedConcepts: Math.round(innovationIdeasData.metadata.totalIdeas * completion),
          scoringDimensions: Math.round(5 * completion)
        };
      case 'LLM Enhancement':
        return {
          enhancedIdeas: Math.round(innovationIdeasData.metadata.totalIdeas * completion),
          implementationSteps: Math.round(5 * completion)
        };
      default:
        return { progress: completion };
    }
  };

  const handleComplete = () => {
    console.log('TrizIntegrationPhase - handleComplete - parsedTrizData state:', {
      parsedTrizDataExists: !!parsedTrizData,
      parsedTrizDataKeys: parsedTrizData ? Object.keys(parsedTrizData) : [],
      top10IdeasExists: !!parsedTrizData?.top10Ideas,
      top10IdeasType: typeof parsedTrizData?.top10Ideas,
      top10IdeasIsArray: Array.isArray(parsedTrizData?.top10Ideas),
      top10IdeasLength: parsedTrizData?.top10Ideas?.length,
      top10IdeasContent: parsedTrizData?.top10Ideas
    });
    
    const resultData = {
      contradictionMatrix: generateContradictions(),
      generatedIdeas: generatedIdeas,
      trizAgentResponse: trizAgentResponse,
      top10Ideas: parsedTrizData?.top10Ideas || [],
      marketResearch: parsedTrizData?.marketResearch || {},
      keyValuePoints: parsedTrizData?.keyValuePoints || [],
      businessCase: parsedTrizData?.businessCase || '',
      parsingError: parsingError,
      summary: {
        totalIdeas: generatedIdeas.length,
        avgInnovationScore: generatedIdeas.reduce((sum, idea) => sum + idea.innovationScore, 0) / generatedIdeas.length,
        topCategories: ['Core Technology', 'Security Innovation', 'User Experience', 'Network Architecture'],
        recommendedPrinciples: trizPrinciples.slice(0, 3).map(p => p.name),
        innovationBreakthroughs: [
          'Homomorphic PII processing pipeline',
          'Quantum-resistant privacy protection',
          'Real-time compliance automation',
          'Federated privacy learning network'
        ]
      }
    };
    
    console.log('TrizIntegrationPhase - handleComplete data:', {
      hasTop10Ideas: resultData.top10Ideas.length > 0,
      top10IdeasCount: resultData.top10Ideas.length,
      top10IdeasRanks: resultData.top10Ideas.map((idea: Top10Idea) => idea.rank).sort((a, b) => a - b),
      top10Ideas: resultData.top10Ideas,
      top10IdeasWithScores: resultData.top10Ideas.map((idea: Top10Idea) => ({
        name: idea.name,
        rank: idea.rank,
        overall_score: idea.overall_score,
        individual_scores: idea.individual_scores,
        key_strengths: idea.key_strengths,
        areas_for_improvement: idea.areas_for_improvement
      })),
      marketResearch: resultData.marketResearch,
      keyValuePoints: resultData.keyValuePoints,
      parsedTrizDataAvailable: !!parsedTrizData,
      parsedTrizDataTop10Count: parsedTrizData?.top10Ideas?.length || 0,
      generatedIdeasCount: generatedIdeas.length,
      resultDataKeys: Object.keys(resultData),
      // Additional debugging for the complete data flow
      originalParsedTop10Ideas: parsedTrizData?.top10Ideas?.map((idea: Top10Idea) => ({
        rank: idea.rank,
        name: idea.name,
        overall_score: idea.overall_score
      })),
      resultTop10Ideas: resultData.top10Ideas.map((idea: Top10Idea) => ({
        rank: idea.rank,
        name: idea.name,
        overall_score: idea.overall_score
      }))
    });
    
    onComplete(resultData);
  };

  const startTrizIntegration = () => {
    if (TEST_MODE) {
      console.log('🧪 TEST MODE: Skipping API call, going directly to local file parsing');
      // Skip API call entirely and go directly to parsing local file
      setIsProcessing(true);
      setStageProgress(0);
      setCurrentStage(0);
      fetchAndParseLocalFile();
    } else {
      console.log('🚀 PRODUCTION MODE: Starting TRIZ agent API call');
      getTrizAgentResponse();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-4xl lg:text-5xl sqsft-heading-1 mb-2">TRIZ Integration & Innovation</h2>
            <p className="text-lg sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>Apply TRIZ principles for systematic innovation</p>
          </div>
        </div>
        
        {!isProcessing && !isIntegrationCompleted && generatedIdeas.length === 0 && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startTrizIntegration}
            className="sqsft-btn-primary"
          >
            Start TRIZ Integration
          </motion.button>
        )}
        
        {(isIntegrationCompleted || generatedIdeas.length > 0) && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleComplete}
            className="sqsft-btn-primary"
          >
            Complete Phase
          </motion.button>
        )}
      </div>

      {/* Processing Status */}
      {(isProcessing || (isTrizAgentApiCallLoading && stageProgress > 0)) && (
        <div className="space-y-6">
          <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="sqsft-body font-medium">
                  Stage {currentStage + 1}/7: {trizIdeasStages[currentStage]?.name}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="font-bold text-lg" style={{ color: 'rgb(var(--color-deep-green))' }}>
                  {stageProgress >= 100 ? '100' : stageProgress.toFixed(0)}%
                </span>
                {stageProgress >= 100 && (
                  <span className="text-sm text-green-600 font-medium">Complete!</span>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <motion.div
                className="h-3 rounded-full"
                style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}
                initial={{ width: 0 }}
                animate={{ width: `${stageProgress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
          
          {/* Streaming Results */}
          {Object.keys(streamingResults).length > 0 && (
            <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
              <h4 className="sqsft-heading-4 mb-4">Live Results Stream</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(streamingResults).map(([stage, data]: [string, any]) => (
                  <div key={stage} className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">{stage}</div>
                    <div className="space-y-1">
                      {Object.entries(data).map(([key, value]: [string, any]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-600">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completion Status */}
      {(isIntegrationCompleted || generatedIdeas.length > 0) && !isProcessing && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <h3 className="sqsft-heading-3" style={{ color: 'rgb(var(--color-deep-green))' }}>TRIZ Integration Complete!</h3>
              <p className="sqsft-caption" style={{ color: 'rgb(var(--color-deep-green))' }}>
                Generated {generatedIdeas.length} innovative solutions. {isIntegrationCompleted ? "Ready to proceed to next phase." : "Review results below."}
              </p>
            </div>
          </div>
        </div>
      )}

      {!isProcessing && !isIntegrationCompleted && generatedIdeas.length === 0 ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="sqsft-feature-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                    <span className="sqsft-gradient-text">Live Integration Process</span>
                </h3>
                <div className="rounded-xl p-4 h-96 overflow-y-auto font-mono text-sm" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center mb-4">
                            <Cpu className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="sqsft-heading-4 mb-2">TRIZ Engine</h4>
                        <p className="sqsft-body max-w-xs" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            The live innovation process will be shown here. Click "Start TRIZ Integration" to begin.
                        </p>
                    </div>
                </div>
            </div>
            <div className="sqsft-feature-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                    <span className="sqsft-gradient-text">Integration Framework</span>
                </h3>
                <div className="h-96 overflow-y-auto pr-2">
                    <div>
                        <h4 className="sqsft-heading-4 mb-3">Core Integration Stages</h4>
                        <div className="space-y-3">
                            {stages.map((stage, index) => (
                                <motion.div
                                    key={stage.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="sqsft-feature-card p-4 border hover:border-sage-green transition-all duration-300"
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                                            <stage.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className="sqsft-heading-4">{stage.name}</h5>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-6">
        {/* Process Logs */}
        {(isProcessing || trizAgentResponse || generatedIdeas.length > 0) && (
          <div className="sqsft-feature-card p-6">
            <h3 className="sqsft-heading-3 mb-4">TRIZ Process Logs</h3>
            <div className="bg-slate-100/80 rounded-xl h-96 overflow-y-auto" ref={trizAgentLogsRef}>
              <div className="p-4 font-mono text-sm triz-agent-logs">
                  {trizAgentResponse && trizAgentResponse.trim() !== '' ? (
                    <div>
                      <ReactMarkdown>{trizAgentResponse}</ReactMarkdown>
                    </div>
                  ) : generatedIdeas.length > 0 ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading detailed analysis logs...</p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Processing TRIZ analysis...</p>
                    </div>
                  )}
              </div>
            </div>
          
          {/* GCS File Processing Status */}
          {isParsingGcsFile && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-blue-700">Parsing TRIZ analysis from GCS file...</span>
              </div>
            </div>
          )}
          
          {/* Removed the GCS file error display as requested */}
          </div>
        )}
      </div>
      )}


      {/* Generated Ideas */}
      <AnimatePresence>
        {generatedIdeas.length > 0 && !isTrizAgentApiCallLoading && !isParsingGcsFile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Sparkles className="w-6 h-6" style={{ color: 'rgb(var(--color-deep-green))' }} />
                <h3 className="sqsft-heading-3">Generated Innovation Ideas</h3>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  Showing all {generatedIdeas.length} ideas
                </span>
                {trizAgentResponseGcsUrl && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                    From TRIZ Agent Analysis
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 max-h-[600px] overflow-y-scroll">
              {generatedIdeas.map((idea) => (
                <motion.div
                  key={idea.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/60 rounded-xl p-4 border border-slate-200/50 hover:border-green-300 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
                  onClick={() => setSelectedIdea(idea)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-gray-900 font-semibold text-sm mb-1">{idea.title}</h4>
                      <p className="text-gray-600 text-xs mb-2 line-clamp-2">{idea.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {idea.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Idea Detail Modal */}
      <AnimatePresence>
        {selectedIdea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedIdea(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-2xl max-h-[80vh] overflow-y-auto border border-slate-200 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedIdea.title}</h2>
                  <p className="text-gray-600">{selectedIdea.description}</p>
                </div>
                <button
                  onClick={() => setSelectedIdea(null)}
                  className="text-gray-500 hover:text-gray-900 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-gray-900 font-semibold mb-2">TRIZ Reasoning</h3>
                  <p className="text-gray-700 text-sm">{selectedIdea.reasoning}</p>
                </div>
                
                <div>
                  <h3 className="text-gray-900 font-semibold mb-2">Implementation Steps</h3>
                  <ul className="space-y-2">
                    {selectedIdea.implementationSteps.map((step, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-700 text-sm">
                        <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrizIntegrationPhase;